// اقدام‌های چرخه عمر عضویت (US-IAM-012، US-IAM-014؛ SM-Membership): ارسال مجدد دعوت، تعلیق، فعال‌سازی و لغو با ReasonDialog
// و نمایش اثر (ارائه‌دهنده بودن، تعداد نوبت‌های آینده، تنها مالک — AC-IAM-014.2). مشترک SCR-STAFF-920 و 921.
// AP-IAM-03: خروج اجباری از همه نشست‌ها (US-IAM-051، iam.session.revoke) و بازنشانی 2FA فقط مالک (US-IAM-003،
// iam.member.reset_2fa — C-AGENT-386)، هر دو با دلیل الزامی و Audit.
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import { unwrap, type MembershipView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ConfirmDialog, faDigits, LoadingState, ReasonDialog } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { iamKeys } from './common';

type ReasonAction = 'suspend' | 'reactivate' | 'revoke' | 'signout' | 'reset_2fa';
type Action = ReasonAction | 'resend';

/** اقدام‌های مجاز هر وضعیت (SM-Membership در PRD-IAM §6). */
export function allowedActions(status: MembershipView['status']): Action[] {
  switch (status) {
    case 'Invited':
      return ['resend', 'revoke'];
    case 'InvitationExpired':
      return ['resend'];
    case 'Active':
      return ['suspend', 'revoke'];
    case 'Suspended':
      return ['reactivate', 'revoke'];
    default:
      return [];
  }
}

/** پیش‌نمایش اثر تعلیق یا لغو (GET suspension-impact). */
function ImpactNotice({ membershipId }: { membershipId: string }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const impact = useQuery({
    queryKey: iamKeys.impact(session?.tenantId, membershipId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/identity/memberships/{id}/suspension-impact', {
          params: { path: { id: membershipId } },
        }),
      ),
  });
  if (impact.isPending) {
    return <LoadingState />;
  }
  if (!impact.data) {
    return null;
  }
  const bookings = Number(impact.data.futureBookings);
  return (
    <Stack spacing={1} sx={{ mb: 2 }} data-testid="suspension-impact" data-future-bookings={bookings}>
      {impact.data.isLastOwner && (
        <Alert severity="error" data-testid="impact-last-owner">
          {t('iam:actions.impact.last_owner')}
        </Alert>
      )}
      {impact.data.isProvider && (
        <Alert severity="warning" data-testid="impact-provider">
          {bookings > 0
            ? t('iam:actions.impact.provider_bookings', { value: faDigits(bookings) })
            : t('iam:actions.impact.provider_no_bookings')}
        </Alert>
      )}
      {!impact.data.isProvider && !impact.data.isLastOwner && (
        <Alert severity="info">{t('iam:actions.impact.sessions')}</Alert>
      )}
    </Stack>
  );
}

export interface MemberActionsProps {
  member: MembershipView;
}

export function MemberActions({ member }: MemberActionsProps) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const invite = useWriteAccess('iam.member.invite');
  const manage = useWriteAccess('iam.member.manage');
  const menuId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const revokeSessions = useWriteAccess('iam.session.revoke');
  const resetMfa = useWriteAccess('iam.member.reset_2fa');
  const security: Action[] = [];
  if (member.status === 'Active' || member.status === 'Suspended') {
    if (revokeSessions.canWrite) {
      security.push('signout');
    }
    if (resetMfa.canWrite) {
      security.push('reset_2fa');
    }
  }
  const actions = [
    ...allowedActions(member.status).filter((item) => (item === 'resend' ? invite.canWrite : manage.canWrite)),
    ...security,
  ];

  const run = useMutation({
    mutationFn: async ({ kind, reason }: { kind: Action; reason?: string }) => {
      const path = { params: { path: { id: member.membershipId } } };
      if (kind === 'resend') {
        return unwrap(tenantApi.POST('/api/v1/identity/memberships/{id}:resend-invitation', path));
      }
      const body = { reason: reason ?? '' };
      if (kind === 'suspend') {
        return unwrap(tenantApi.POST('/api/v1/identity/memberships/{id}:suspend', { ...path, body }));
      }
      if (kind === 'signout') {
        await unwrap(
          tenantApi.POST('/api/v1/identity/memberships/{id}/sessions:revoke-all', { ...path, body }),
        );
        return null;
      }
      if (kind === 'reset_2fa') {
        await unwrap(tenantApi.POST('/api/v1/identity/memberships/{id}/2fa:reset', { ...path, body }));
        return null;
      }
      if (kind === 'reactivate') {
        return unwrap(tenantApi.POST('/api/v1/identity/memberships/{id}:reactivate', { ...path, body }));
      }
      return unwrap(tenantApi.POST('/api/v1/identity/memberships/{id}:revoke', { ...path, body }));
    },
    onSuccess: (_data, { kind }) => {
      setAction(null);
      setNotice(t(`iam:actions.done.${kind}`));
      void queryClient.invalidateQueries({ queryKey: iamKeys.all(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: ['identity', 'me', 'access'] });
    },
  });

  const open = (kind: Action) => {
    setAnchor(null);
    run.reset();
    setAction(kind);
  };

  if (actions.length === 0) {
    return null;
  }

  const reasonAction: ReasonAction | null = action === 'resend' || action === null ? null : action;

  // رویدادهای Menu و دیالوگ (Portal) در درخت React به ردیف جدول می‌رسند؛ نباید ردیف را باز کنند.
  return (
    <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
      <IconButton
        aria-label={t('iam:actions.menu', { name: member.displayName })}
        aria-haspopup="menu"
        aria-controls={anchor ? menuId : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        data-testid={`member-actions-${member.membershipId}`}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu id={menuId} anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {actions.map((kind) => (
          <MenuItem key={kind} onClick={() => open(kind)} data-testid={`member-action-${kind}`}>
            <ListItemText primary={t(`iam:actions.${kind}`)} />
          </MenuItem>
        ))}
      </Menu>
      <ConfirmDialog
        open={action === 'resend'}
        titleKey="iam:actions.resend_title"
        messageKey="iam:actions.resend_message"
        confirmKey="iam:actions.resend"
        busy={run.isPending}
        error={run.error ?? undefined}
        onConfirm={() => run.mutate({ kind: 'resend' })}
        onCancel={() => setAction(null)}
      />
      <ReasonDialog
        open={reasonAction !== null}
        titleKey={`iam:actions.${reasonAction ?? 'suspend'}_title`}
        messageKey={`iam:actions.${reasonAction ?? 'suspend'}_message`}
        confirmKey={`iam:actions.${reasonAction ?? 'suspend'}`}
        destructive={reasonAction !== 'reactivate'}
        busy={run.isPending}
        error={run.error ?? undefined}
        maxLength={500}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => reasonAction && run.mutate({ kind: reasonAction, reason })}
      >
        {(reasonAction === 'suspend' || reasonAction === 'revoke') && (
          <ImpactNotice membershipId={member.membershipId} />
        )}
      </ReasonDialog>
      <Snackbar
        open={notice !== null}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        message={notice}
        data-testid="member-action-notice"
      />
    </span>
  );
}
